// ── GroupBuyTeaser — 홈 대시보드 공동구매 카드 ──
// Firestore groupBuyings 실시간 구독 → Top 3 모집 중 카드.
// 데이터 없으면 "곧 출시" 안내 + 관심 카테고리 옵트인.
// 카드 클릭 → 공동구매 페이지로 이동.

import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../db';
import {
  sortGroupBuyings, effectiveStatus, calcSavings, daysUntilDeadline,
  formatKRW, GB_STATUS,
} from '../utils/groupBuying';
import {
  GROUPBUY_CATEGORIES, loadInterest, saveInterest, selectedCategoriesCount,
} from '../utils/groupBuyInterest';

export default function GroupBuyTeaser({ isMobile, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [interest, setInterest] = useState(loadInterest);

  useEffect(() => {
    const q = query(collection(db, 'groupBuyings'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.warn('GroupBuyTeaser 구독 실패:', err.message);
      setLoading(false);
    });
    return unsub;
  }, []);

  const activeTop = sortGroupBuyings(items)
    .filter(gb => {
      const s = effectiveStatus(gb);
      return s === 'open' || s === 'closing';
    })
    .slice(0, 3);

  const refreshInterest = () => setInterest(loadInterest());
  const isRegistered = interest.registered === true;
  const selectedCount = selectedCategoriesCount(interest);
  const goToPage = () => onNavigate && onNavigate('groupBuying');

  // 데이터 없을 때 — 옛 teaser
  if (!loading && activeTop.length === 0) {
    return (
      <>
        <div style={{
          background: '#F4F4F5',
          borderRadius: 16,
          padding: isMobile ? '18px 16px' : '22px 26px',
          marginBottom: 18,
          border: '1px solid #D4D4D8',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 12, right: 14,
            padding: '3px 10px', borderRadius: 12,
            background: '#EA580C', color: '#fff',
            fontSize: 10, fontWeight: 800,
          }}>BETA · 곧 출시</div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 900, color: '#78350f' }}>
              🤝 공동구매로 식자재 -10~25%
            </div>
            <div style={{ fontSize: 12, color: '#52525B', marginTop: 4, lineHeight: 1.6 }}>
              여러 매장이 함께 주문하면 식자재·일회용품 가격이 확 떨어져요.
              지금 관심 등록하면 첫 회차 모집 알림 보내드릴게요.
            </div>
          </div>

          {!isRegistered ? (
            <button onClick={() => setModalOpen(true)} style={{
              width: '100%', padding: '12px', border: 'none',
              background: '#EA580C',
              color: '#fff', fontSize: 14, fontWeight: 800,
              borderRadius: 10, cursor: 'pointer',
              boxShadow: '0 3px 12px rgba(217,119,6,0.35)',
            }}>🔔 관심 등록하고 첫 회차 알림 받기</button>
          ) : (
            <div style={{
              padding: '10px 14px', background: '#fff',
              border: '1.5px solid #FF6F0F', borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 8,
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#18181B' }}>
                  ✅ 관심 등록 완료
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  관심 카테고리 {selectedCount}개 — 첫 회차 출시 시 알림
                </div>
              </div>
              <button onClick={() => setModalOpen(true)} style={{
                padding: '6px 12px', border: '1px solid #FF6F0F',
                background: '#fff', color: '#18181B',
                fontSize: 11, fontWeight: 700, borderRadius: 7, cursor: 'pointer',
              }}>✏️ 카테고리 수정</button>
            </div>
          )}
        </div>

        {modalOpen && (
          <InterestModal interest={interest} isMobile={isMobile}
            onClose={() => setModalOpen(false)}
            onSaved={() => { refreshInterest(); setModalOpen(false); }} />
        )}
      </>
    );
  }

  // 데이터 있을 때 — 실제 카드
  return (
    <div style={{
      background: '#F4F4F5',
      borderRadius: 16,
      padding: isMobile ? '18px 16px' : '22px 26px',
      marginBottom: 18,
      border: '1px solid #D4D4D8',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 900, color: '#78350f' }}>
            🛒 진행 중인 공동구매
          </div>
          <div style={{ fontSize: 12, color: '#52525B', marginTop: 4 }}>
            셈핏이 큐레이션한 식자재 — 시세 대비 저렴하게 함께 구매
          </div>
        </div>
        <button onClick={goToPage} style={{
          padding: '8px 14px', background: '#fff',
          border: '1.5px solid #EA580C', color: '#52525B',
          borderRadius: 8, fontSize: 12, fontWeight: 700,
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}>전체 보기 →</button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 10,
      }}>
        {activeTop.map(gb => (
          <RealCard key={gb.id} gb={gb} onClick={goToPage} />
        ))}
      </div>

      {!isRegistered && (
        <button onClick={() => setModalOpen(true)} style={{
          marginTop: 12, padding: '8px 14px',
          background: 'transparent', border: '1px dashed #EA580C',
          color: '#52525B', borderRadius: 8,
          fontSize: 11, fontWeight: 600, cursor: 'pointer', width: '100%',
        }}>🔔 새 공동구매 알림 받기</button>
      )}

      {modalOpen && (
        <InterestModal interest={interest} isMobile={isMobile}
          onClose={() => setModalOpen(false)}
          onSaved={() => { refreshInterest(); setModalOpen(false); }} />
      )}
    </div>
  );
}

function RealCard({ gb, onClick }) {
  const eff = effectiveStatus(gb);
  const status = GB_STATUS[eff] || GB_STATUS.open;
  const sav = calcSavings(gb);
  const days = daysUntilDeadline(gb.deadline);

  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 10,
      padding: '10px 12px', border: '1px solid #D4D4D8',
      cursor: 'pointer', transition: 'transform 0.15s ease',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 5,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: status.color,
          background: status.bg, padding: '2px 7px', borderRadius: 8,
        }}>
          {status.label}
          {eff === 'closing' && days !== null && ` · D-${days}`}
        </span>
        {sav.percent > 0 && (
          <span style={{ fontSize: 12, fontWeight: 900, color: '#52525B' }}>
            -{sav.percent}%
          </span>
        )}
      </div>

      <div style={{
        fontSize: 13, fontWeight: 700, color: '#1a1a1a',
        marginBottom: 4, lineHeight: 1.4,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      }}>{gb.title || gb.productName}</div>

      <div style={{ marginBottom: 5 }}>
        {gb.marketPrice > 0 && (
          <span style={{
            fontSize: 10, color: '#9ca3af',
            textDecoration: 'line-through', marginRight: 4,
          }}>{formatKRW(gb.marketPrice)}원</span>
        )}
        <span style={{ fontSize: 14, fontWeight: 900, color: '#EA580C' }}>
          {formatKRW(gb.pricePerUnit)}원
        </span>
        {gb.unit && (
          <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 4 }}>
            / {gb.unit}
          </span>
        )}
      </div>

      <div style={{ fontSize: 10, color: '#6b7280' }}>
        {gb.deadline && `마감 ${gb.deadline.slice(5).replace('-', '/')}`}
        {gb.supplier && ` · ${gb.supplier}`}
      </div>
    </div>
  );
}

function InterestModal({ interest, isMobile, onClose, onSaved }) {
  const [draft, setDraft] = useState(interest);

  const toggle = (catId) => {
    setDraft(d => ({
      ...d,
      categories: { ...d.categories, [catId]: !d.categories[catId] },
    }));
  };

  const handleSave = () => {
    const selectedCount = Object.values(draft.categories || {}).filter(Boolean).length;
    if (selectedCount === 0) {
      alert('최소 1개 카테고리를 선택해주세요');
      return;
    }
    const updated = {
      ...draft,
      registered: true,
      registeredAt: draft.registeredAt || new Date().toISOString(),
    };
    saveInterest(updated);
    onSaved();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16,
        padding: isMobile ? '20px 18px' : '24px 26px',
        maxWidth: 480, width: '100%',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 12px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14,
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1a1a1a' }}>
              🔔 공동구매 관심 등록
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              관심 카테고리에 새 안건 모집 시 알림 보내드려요
            </div>
          </div>
          <button onClick={onClose} aria-label="닫기" style={{
            width: 30, height: 30, borderRadius: '50%',
            border: '1px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 16, cursor: 'pointer',
          }}>×</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 700,
                          display: 'block', marginBottom: 8 }}>
            관심 카테고리 (복수 선택 가능)
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {GROUPBUY_CATEGORIES.map(cat => {
              const checked = !!draft.categories[cat.id];
              return (
                <label key={cat.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px',
                  background: checked ? '#F4F4F5' : '#fafbfc',
                  border: `1.5px solid ${checked ? '#EA580C' : '#e5e7eb'}`,
                  borderRadius: 8, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={checked}
                    onChange={() => toggle(cat.id)}
                    style={{ accentColor: '#EA580C' }} />
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{cat.icon}</span>
                  <span style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
                      {cat.label}
                    </div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
                      {cat.desc}
                    </div>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div style={{
          padding: '10px 12px', background: '#f0f9ff',
          borderRadius: 8, fontSize: 11, color: '#0369a1',
          lineHeight: 1.6, marginBottom: 14,
        }}>
          💡 정보는 본인 단말에만 저장돼요. 새 공동구매 출시 시 알림으로 안내드립니다.
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '11px',
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#6b7280', fontSize: 13, fontWeight: 700,
            borderRadius: 9, cursor: 'pointer',
          }}>취소</button>
          <button onClick={handleSave} style={{
            flex: 2, padding: '11px', border: 'none',
            background: '#EA580C',
            color: '#fff', fontSize: 13, fontWeight: 800,
            borderRadius: 9, cursor: 'pointer',
          }}>✅ 관심 등록 완료</button>
        </div>
      </div>
    </div>
  );
}
